import { ReactNode, useState, useEffect, createContext, useContext, useCallback } from 'react'
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip,
} from '@mui/material'
import {
  AccountCircle,
  Search,
  Menu as MenuIcon,
  Logout,
  ManageAccounts,
  Close,
  FilterAlt,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import {
  Sidebar,
  SIDEBAR_WIDTH_FULL,
  SIDEBAR_WIDTH_ICONS,
  type SidebarCollapseMode,
} from '@components/navigation/Sidebar'
import { getNavTitleForPath } from '@components/navigation/navItems'
import { useFullscreen } from '@hooks/useFullscreen'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'

const SIDEBAR_COLLAPSE_KEY = 'sidebarCollapseMode'

/** Shown when a route matches no nav item and the page sets no title of its own. */
const FALLBACK_PAGE_TITLE = 'Admin Portal'

// Context for sidebar width
interface SidebarContextType {
  sidebarWidth: number
}

const SidebarContext = createContext<SidebarContextType>({ sidebarWidth: SIDEBAR_WIDTH_FULL })

export const useSidebarWidth = () => useContext(SidebarContext)

// Context for page title
interface PageTitleContextType {
  pageTitle: string
  setPageTitle: (title: string) => void
}

const PageTitleContext = createContext<PageTitleContextType>({
  pageTitle: FALLBACK_PAGE_TITLE,
  setPageTitle: () => {},
})

/**
 * Sets the AppBar (and browser tab) title for the current page.
 *
 * A page that does not call this is NOT stuck with the previous page's title any more: the
 * layout falls back to the matching sidebar entry's label — see getNavTitleForPath.
 */
export const usePageTitle = (title?: string) => {
  const { pageTitle, setPageTitle } = useContext(PageTitleContext)

  useEffect(() => {
    if (title) {
      setPageTitle(title)
    }
  }, [title, setPageTitle])

  return { pageTitle, setPageTitle }
}

/**
 * Name, email and role chips at the top of the account menu. A component (not inline JSX) only
 * so it can carry `muiSkipListHighlight`: MenuList would otherwise hand the menu's initial focus
 * to this non-interactive block instead of the first real item.
 */
function AccountMenuHeader({ displayName, email, roles }: { displayName: string; email?: string; roles: string[] }) {
  return (
    <Box sx={{ px: 2, pt: 1, pb: 1.5 }} onClick={(e) => e.stopPropagation()}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
        {displayName}
      </Typography>
      {email && email !== displayName && (
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {email}
        </Typography>
      )}
      {roles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {roles.map((role) => (
            <Chip key={role} label={role} size="small" variant="outlined" />
          ))}
        </Box>
      )}
    </Box>
  )
}
AccountMenuHeader.muiSkipListHighlight = true

interface MainLayoutProps {
  children: ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const { logout, user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) // <960px: temporary drawer
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')) // <600px
  const searchDialogProps = useMobileDialogProps()
  const roles = user?.roles ?? []
  const canOpenUsers = roles.includes('Admin')
  const userId = user?.userId || user?.id
  const displayName =
    user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Signed in'

  // Hides the header and the rail, and asks the browser for true fullscreen where that exists.
  // On a phone the two together are worth roughly two hundred vertical pixels, which is the
  // difference between a table showing three rows and showing eight.
  const fullscreen = useFullscreen()

  /**
   * The title a page pushed, stamped with the path it was pushed FOR.
   *
   * Stamping matters because effects run child-first: on navigation the new page sets its title
   * before the layout could react to the route change, so anything that cleared the title in a
   * layout effect would wipe the title the page just set. Comparing paths instead means a stale
   * title simply stops matching and the nav-derived fallback takes over.
   */
  const [titleEntry, setTitleEntry] = useState<{ path: string; title: string } | null>(null)

  const setPageTitle = useCallback((title: string) => {
    setTitleEntry({ path: window.location.pathname, title })
  }, [])

  const pageTitle =
    titleEntry && titleEntry.path === location.pathname
      ? titleEntry.title
      : (getNavTitleForPath(location.pathname) ?? FALLBACK_PAGE_TITLE)

  // Update browser tab title when pageTitle changes
  useEffect(() => {
    document.title = `HiddenAdv - ${pageTitle}`
  }, [pageTitle])

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapseMode, setSidebarCollapseMode] = useState<SidebarCollapseMode>(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSE_KEY)
    return (saved ? parseInt(saved, 10) : 2) as SidebarCollapseMode
  })

  const handleSidebarCollapseModeChange = (mode: SidebarCollapseMode) => {
    setSidebarCollapseMode(mode)
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, mode.toString())

    // Dispatch resize events during and after transition to trigger map resize
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 250)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 400)
  }

  // Get current sidebar width based on collapse mode
  const currentSidebarWidth =
    sidebarCollapseMode === 0 ? 0 : sidebarCollapseMode === 1 ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  // Close the mobile drawer whenever the route changes. The nav buttons close it themselves,
  // but this also covers in-page navigation and the browser Back button.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Clear search query when navigating away from search results page or to advanced search
  useEffect(() => {
    if (!location.pathname.startsWith('/search') || location.pathname === '/search/advanced') {
      setSearchQuery('')
    }
  }, [location.pathname])

  const handleDrawerToggle = () => {
    setMobileOpen((open) => !open)
  }

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null)
  }

  const handleLogout = () => {
    handleAccountMenuClose()
    logout()
  }

  // Live search from the AppBar field (desktop). Skipped while the phone search dialog is open:
  // there it would navigate the page hidden BEHIND the dialog on every pause in typing, so the
  // dialog navigates only on submit.
  useEffect(() => {
    if (!searchQuery.trim() || searchDialogOpen) {
      return
    }

    const timer = setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, searchDialogOpen, navigate])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const submitSearch = () => {
    if (!searchQuery.trim()) return
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    setSearchDialogOpen(false)
    setSearchQuery('')
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submitSearch()
    }
  }

  const closeSearchDialog = () => {
    setSearchDialogOpen(false)
    setSearchQuery('')
  }

  const handleSearchIconClick = () => {
    if (isSmallScreen) {
      setSearchDialogOpen(true)
    } else {
      navigate('/search/advanced')
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        // The shell never scrolls; only the main region does.
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* Header spans full width. Unmounted rather than hidden in immersive mode, so its height
          is genuinely reclaimed instead of merely being invisible. */}
      {!fullscreen.isImmersive && (
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'primary.main',
          zIndex: (t) => t.zIndex.drawer + 1,
          flexShrink: 0,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 1 }}>
          {/* Left: Hamburger Menu + page title */}
          {/* Capped from sm up: a long page title (a ticket subject) otherwise out-muscles the
              search field and squeezes it to a sliver. It ellipsizes instead. On a phone the
              search field is hidden, so the title may use the whole bar. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flex: '0 1 auto',
              maxWidth: { sm: '40%', lg: '45%' },
            }}
          >
            {/* Hamburger - always on mobile, on desktop only while the rail is hidden */}
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Toggle navigation"
              onClick={isMobile ? handleDrawerToggle : () => handleSidebarCollapseModeChange(2)}
              sx={{
                display: {
                  xs: 'inline-flex',
                  md: sidebarCollapseMode === 0 ? 'inline-flex' : 'none',
                },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              noWrap
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {pageTitle}
            </Typography>
          </Box>

          {/* Right: Search Bar + User Actions */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 0.5, sm: 2 },
              alignItems: 'center',
              flex: 1,
              justifyContent: 'flex-end',
              minWidth: 0,
            }}
          >
            {/* Search Bar - show on larger screens */}
            <TextField
              placeholder="Search adventures, users, groups..."
              size="small"
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onBlur={() => setSearchQuery('')}
              sx={{
                display: { xs: 'none', sm: 'block' },
                flex: 1,
                minWidth: { sm: 180 },
                maxWidth: { sm: 300, md: 450, lg: 600 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 1,
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '8.5px 14px',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Advanced search"
                      onClick={() => navigate('/search/advanced')}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <FilterAlt fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Search Icon - small screens only, where the search field is hidden */}
            {/* Top-bar buttons keep the default size (40px + padding) rather than size="small"
                (~34px): they are the most-tapped targets on a phone. */}
            <IconButton
              color="inherit"
              aria-label="Search"
              onClick={handleSearchIconClick}
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <Search />
            </IconButton>
            <Tooltip title="Full screen">
              <IconButton
                color="inherit"
                aria-label="Enter full screen"
                onClick={fullscreen.toggle}
              >
                <Fullscreen />
              </IconButton>
            </Tooltip>
            <IconButton
              color="inherit"
              edge="end"
              aria-label="Account"
              aria-haspopup="menu"
              onClick={handleAccountMenuOpen}
            >
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      )}

      {/* Account Menu */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        onClick={handleAccountMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 220,
              maxWidth: 'calc(100vw - 16px)',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {/* Who is signed in, and with what roles. */}
        <AccountMenuHeader displayName={displayName} email={user?.email} roles={roles} />
        <Divider />
        {/* The admin user page for yourself: sessions, MFA, subscription. Only for roles that
            can open /users at all. */}
        {canOpenUsers && userId && (
          <MenuItem onClick={() => navigate(`/users/${userId}`)} sx={{ minHeight: 44 }}>
            <ListItemIcon>
              <ManageAccounts fontSize="small" />
            </ListItemIcon>
            <ListItemText>My user record</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout} sx={{ minHeight: 44 }}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Content area with sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', width: '100%', minWidth: 0 }}>
        {/* The rail goes too. On desktop it is 240px of the width the tables wanted; on mobile it
            is already a drawer, so this costs nothing there. */}
        {!fullscreen.isImmersive && (
          <Sidebar
            mobileOpen={mobileOpen}
            onDrawerToggle={handleDrawerToggle}
            isMobile={isMobile}
            collapseMode={sidebarCollapseMode}
            onCollapseModeChange={handleSidebarCollapseModeChange}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            flex: 1,
            // minWidth:0 is what stops a wide child from stretching the flex row (and with it
            // the whole page) instead of scrolling inside its own container.
            minWidth: 0,
            maxWidth: '100%',
            backgroundColor: 'background.default',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <PageTitleContext.Provider value={{ pageTitle, setPageTitle }}>
            <SidebarContext.Provider value={{ sidebarWidth: currentSidebarWidth }}>
              {children}
            </SidebarContext.Provider>
          </PageTitleContext.Provider>
        </Box>
      </Box>

      {/* The only way back, so it is deliberately always on top and never scrolls away. Kept small
          and semi-transparent until touched: it sits over page content, and the whole point of the
          mode is that content gets the screen. */}
      {fullscreen.isImmersive && (
        <Tooltip title="Exit full screen (Esc)">
          <IconButton
            aria-label="Exit full screen"
            onClick={fullscreen.exit}
            sx={{
              position: 'fixed',
              top: 8,
              right: 8,
              zIndex: (t) => t.zIndex.tooltip + 1,
              bgcolor: 'background.paper',
              boxShadow: 3,
              opacity: 0.6,
              '&:hover, &:focus-visible': { opacity: 1, bgcolor: 'background.paper' },
            }}
          >
            <FullscreenExit />
          </IconButton>
        </Tooltip>
      )}

      {/* Mobile Search Dialog */}
      <Dialog open={searchDialogOpen} onClose={closeSearchDialog} maxWidth="sm" fullWidth {...searchDialogProps}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <Box component="span" sx={{ flex: 1 }}>
            Search
          </Box>
          <IconButton aria-label="Close search" onClick={closeSearchDialog}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Finds adventures, users and groups by name.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="search"
            placeholder="Search adventures, users, groups..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            inputProps={{ enterKeyHint: 'search', 'aria-label': 'Search' }}
            sx={{ mt: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => {
              closeSearchDialog()
              navigate('/search/advanced')
            }}
            sx={{ minHeight: 44, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            Advanced search
          </Button>
          <Button
            variant="contained"
            disabled={!searchQuery.trim()}
            onClick={submitSearch}
            sx={{ minHeight: 44, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
